import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import { authApi, configApi } from '../api';
import socketService from '../services/socketService';
import permission from './modules/permission';
import menus from './modules/menus';
import remarksSync from './modules/remarksSync';
import tasks from './modules/tasks';

Vue.use(Vuex);

// Token 刷新定时器
let tokenRefreshTimer = null;

/**
 * 持久化配置
 * - key: 存储键名
 * - paths: 只持久化白名单中的状态
 * - storage: 使用 localStorage（长期有效）
 * - 菜单数据持久化后，刷新页面不再重复请求 API
 */
const persistConfig = createPersistedState({
  key: 'vuex_delivery_platform',
  storage: window.localStorage,
  // 白名单：只持久化需要长期保存的状态
  paths: [
    // 全局认证状态
    'token',
    'user',
    'permissions',
    'sidebarCollapsed',
    'systemConfigs',
    'tokenIssuedAt',
    'hasRoutes',  // 重要：持久化路由加载状态
    // 菜单状态（避免刷新后重复请求菜单 API）
    // 注意：不持久化 dynamicRoutes，因为包含组件函数（无法 JSON 序列化）
    'menus.sidebarRouters',
    'menus.menuList',
    'menus.hasLoaded'
  ]
});

const store = new Vuex.Store({
  modules: {
    permission,
    menus,
    remarksSync,
    tasks
  },
  plugins: [persistConfig],
  state: {
    token: '',
    user: {},
    permissions: [],
    tokenIssuedAt: null,  // Token 签发时间戳
    hasRoutes: false,
    sidebarCollapsed: false,
    systemConfigs: {},
    onlineUsers: [],
    unreadNotifications: 0,
    triggerOrderRefresh: 0,
    triggerTaskRefresh: 0,
    availableTaskCount: 0,
    reconciliationFrozen: false,
    frozenMonth: null,
    accountingPeriod: {
      period: null,
      isOpen: false
    }
  },

  getters: {
    token: state => state.token,
    user: state => state.user,
    permissions: state => state.permissions,
    tokenIssuedAt: state => state.tokenIssuedAt,
    hasRoutes: state => state.hasRoutes,
    sidebarRouters: state => state.permission.sidebarRouters,
    userId: state => state.user?.id,
    isLoggedIn: state => !!state.token,
    hasPermission: (state) => (permission) => {
      return state.permissions.includes('/*') || state.permissions.includes(permission);
    },
    hasAnyPermission: (state) => (perms) => {
      if (state.permissions.includes('/*')) return true;
      return perms.some(p => state.permissions.includes(p));
    },
    unreadNotifications: state => state.unreadNotifications,
    systemConfigs: state => state.systemConfigs,
    availableTaskCount: state => state.availableTaskCount,
    reconciliationFrozen: state => state.reconciliationFrozen,
    frozenMonth: state => state.frozenMonth,
    isSuperAdmin: state => (state.user?.roles || []).includes('superadmin'),
    accountingPeriod: state => state.accountingPeriod
  },

  mutations: {
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_TOKEN_ISSUED_AT(state, timestamp) {
      state.tokenIssuedAt = timestamp;
    },
    SET_USER(state, user) {
      // 确保 roles 数组存在（向后兼容：如果只有 role 字符串，转换为数组）
      if (user && !user.roles && user.role) {
        user.roles = [user.role];
      }
      state.user = user;
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },
    SET_HAS_ROUTES(state, hasRoutes) {
      state.hasRoutes = hasRoutes;
    },
    CLEAR_AUTH(state) {
      state.token = '';
      state.tokenIssuedAt = null;
      state.user = {};
      state.permissions = [];
      state.hasRoutes = false;
      state.sidebarCollapsed = false;
      state.systemConfigs = {};
      state.unreadNotifications = 0;
      // 清除定时器
      if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer);
        tokenRefreshTimer = null;
      }
    },
    SET_UNREAD_NOTIFICATIONS(state, count) {
      state.unreadNotifications = count;
    },
    INCREMENT_UNREAD_NOTIFICATIONS(state) {
      state.unreadNotifications++;
    },
    TRIGGER_ORDER_REFRESH(state) {
      state.triggerOrderRefresh = Date.now();
    },
    TRIGGER_TASK_REFRESH(state) {
      state.triggerTaskRefresh = Date.now();
    },
    SET_SYSTEM_CONFIGS(state, configs) {
      const configMap = {};
      configs.forEach(c => configMap[c.config_key] = c);
      state.systemConfigs = configMap;
    },
    UPDATE_SYSTEM_CONFIGS(state, configs) {
      const configMap = {};
      (configs || []).forEach(c => configMap[c.config_key] = c);
      state.systemConfigs = configMap;
    },
    SET_ONLINE_USERS(state, users) {
      state.onlineUsers = users;
    },
    SET_AVAILABLE_TASK_COUNT(state, count) {
      state.availableTaskCount = count;
    },
    SET_RECONCILIATION_FROZEN(state, frozen) {
      state.reconciliationFrozen = frozen;
    },
    SET_FROZEN_MONTH(state, month) {
      state.frozenMonth = month;
    },
    SET_ACCOUNTING_PERIOD(state, { period }) {
      state.accountingPeriod = {
        period,
        isOpen: !!period
      };
    }
  },

  actions: {
    async login({ commit, dispatch }, { username, password }) {
      const res = await authApi.login({ username, password });
      commit('SET_TOKEN', res.data.token);
      commit('SET_TOKEN_ISSUED_AT', Math.floor(Date.now() / 1000));  // 记录签发时间
      commit('SET_USER', res.data.user);
      commit('SET_PERMISSIONS', res.data.user.permissions || []);
      commit('SET_HAS_ROUTES', false);
      dispatch('initSocket');
      dispatch('startTokenRefreshTimer');  // 启动定时刷新
      return res.data;
    },

    async getUserInfo({ commit, dispatch }) {
      const res = await authApi.getCurrentUser();
      commit('SET_USER', res.data);
      commit('SET_PERMISSIONS', res.data.permissions || []);
      dispatch('initSocket');
      // 如果还没有启动定时器，启动它
      if (!tokenRefreshTimer) {
        dispatch('startTokenRefreshTimer');
      }
      return res.data;
    },

    /**
     * 刷新 Token
     * 后端会在 Token 有效期不足 6 小时时才返回新 Token
     */
    async refreshToken({ commit, state }) {
      // 只有在已登录状态下才刷新
      if (!state.token) return;
      
      try {
        const res = await authApi.refreshToken();
        if (res.code === 200) {
          // 如果后端返回了新 Token，更新它
          if (res.data.refreshed) {
            commit('SET_TOKEN', res.data.token);
            commit('SET_TOKEN_ISSUED_AT', Math.floor(Date.now() / 1000));
            console.log('Token 已刷新，有效期延长 24 小时');
          }
        }
      } catch (error) {
        // 如果刷新失败（比如 Token 已过期），不做任何处理
        // 下次请求会 401，然后自动跳转到登录页
        console.error('Token 刷新失败:', error.message);
      }
    },

    /**
     * 启动 Token 定时刷新器
     * 每 6 小时检查一次 Token 是否需要刷新
     */
    startTokenRefreshTimer({ dispatch }) {
      // 清除已有的定时器
      if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer);
      }
      
      // 立即执行一次刷新检查
      dispatch('refreshToken');
      
      // 每 6 小时检查一次（6 * 60 * 60 * 1000 = 21600000 毫秒）
      tokenRefreshTimer = setInterval(() => {
        dispatch('refreshToken');
      }, 6 * 60 * 60 * 1000);
      
      console.log('Token 定时刷新器已启动，每 6 小时检查一次');
    },

    logout({ commit }) {
      socketService.disconnect();
      commit('CLEAR_AUTH');
      
      // 清除权限模块的路由和菜单信息
      commit('permission/SET_ROUTES', []);
      commit('permission/SET_SIDEBAR_ROUTERS', []);
      
      // 清除菜单模块的所有状态
      commit('menus/setDynamicRoutes', []);
      commit('menus/setSidebarRouters', []);
      commit('menus/setMenuList', []);
      commit('menus/setHasLoaded', false);
      
      // 清除其他用户相关状态
      commit('SET_UNREAD_NOTIFICATIONS', 0);
    },

    initSocket() {
      socketService.connect();
    },

    async initSystemConfigs({ commit }) {
      try {
        const res = await configApi.getAll();
        commit('SET_SYSTEM_CONFIGS', res.data || []);
        return res.data;
      } catch (error) {
        console.error('初始化系统配置失败:', error);
        return [];
      }
    },

    async fetchFreezeStatus({ commit }) {
      try {
        const { systemApi } = await import('@/api');
        const res = await systemApi.getFreezeStatus();
        if (res.code === 200) {
          commit('SET_RECONCILIATION_FROZEN', res.data?.frozen || false);
          commit('SET_FROZEN_MONTH', res.data?.frozen_month || null);
        }
      } catch (error) {
        console.error('获取冻结状态失败:', error);
      }
    },

    async fetchAccountingPeriod({ commit }) {
      try {
        const { accountingPeriodApi } = await import('@/api/accounting');
        const res = await accountingPeriodApi.getCurrent();
        if (res.code === 200) {
          commit('SET_ACCOUNTING_PERIOD', {
            period: res.data?.period || null
          });
        }
      } catch (error) {
        console.error('获取核算期状态失败:', error);
      }
    }
  }
});

export default store;
